import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  UserIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { clientsAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

// Types
interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  gender: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  emergencyContact: {
    name: string;
    relationship: string;
    phone: string;
  };
  medicalNotes: string;
  financialInfo: {
    budgetRange: string;
    insuranceProvider?: string;
    policyNumber?: string;
  };
  trackerStatus: 'New Client' | 'Active Client' | 'Target' | 'Placed' | 'Follow-up';
  assignedAgent?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  notes: any[];
  createdAt: string;
  updatedAt: string;
  age: number;
  fullName: string;
  fullAddress: string;
}

const ClientList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [careLevelFilter, setCareLevelFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  const { success: showSuccess, error: showError } = useToast();

  // Fetch clients with filters
  const {
    data: clientsData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['clients', searchTerm, statusFilter, careLevelFilter, sortBy, sortOrder, currentPage],
    queryFn: () => clientsAPI.getClients({
      search: searchTerm,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      careLevel: careLevelFilter !== 'all' ? careLevelFilter : undefined,
      sort: `${sortOrder === 'desc' ? '-' : ''}${sortBy}`,
      page: currentPage,
      limit: itemsPerPage
    }),
    placeholderData: (previousData) => previousData,
  });

  // Filter clients locally for immediate response
  const getFilteredClients = () => {
    if (!(clientsData as any)?.data?.data?.clients || !Array.isArray((clientsData as any).data.data.clients)) return [];
    
    return (clientsData as any).data.data.clients.filter((client: Client) => {
      // Defensive checks for client object
      if (!client || typeof client !== 'object') return false;
      
      const matchesSearch = searchTerm === '' ||
        (client.firstName && client.lastName && `${client.firstName} ${client.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (client.phone && client.phone.includes(searchTerm));
      
      const matchesStatus = statusFilter === 'all' || (client.trackerStatus && client.trackerStatus === statusFilter);
      
      return matchesSearch && matchesStatus;
    });
  };

  const filteredClients = getFilteredClients();
  const totalPages = Math.ceil(((clientsData as any)?.data?.data?.pagination?.totalClients || 0) / itemsPerPage);

  // Helper functions
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'new-client': { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'New Client' },
      'active-client': { color: 'bg-orange-100 text-orange-800 border-orange-200', label: 'Active' },
      'target': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Target' },
      'placed': { color: 'bg-green-100 text-green-800 border-green-200', label: 'Placed' },
      'follow-up': { color: 'bg-purple-100 text-purple-800 border-purple-200', label: 'Follow-up' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig['new-client'];
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getCareLevelBadge = (careLevel: string) => {
    const levelConfig = {
      'independent': { color: 'bg-green-50 text-green-700', label: 'Independent' },
      'assisted': { color: 'bg-blue-50 text-blue-700', label: 'Assisted Living' },
      'memory-care': { color: 'bg-purple-50 text-purple-700', label: 'Memory Care' },
      'skilled-nursing': { color: 'bg-red-50 text-red-700', label: 'Skilled Nursing' }
    };
    
    const config = levelConfig[careLevel as keyof typeof levelConfig] || levelConfig['independent'];
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatBudget = (client: any) => {
    // Handle the actual backend structure
    if (client.financialInfo?.budgetRange) {
      return client.financialInfo.budgetRange;
    }
    
    // Fallback for the form structure if it exists
    if (client.budget?.min && client.budget?.max) {
      return `$${client.budget.min.toLocaleString()} - $${client.budget.max.toLocaleString()}`;
    }
    
    return 'Budget not specified';
  };

  const exportClients = () => {
    // This would typically call an API endpoint to generate and download a CSV/Excel file
    showSuccess('Export functionality will be implemented in the next phase');
  };

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load clients. Please try again.</p>
          <button
            onClick={() => refetch()}
            className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2 text-left">👥 Clients</h1>
          <p className="text-gray-600">
            Manage client profiles and track placement progress
          </p>
        </div>
        <div className="flex gap-3 mt-4 sm:mt-0">
          <button
            onClick={exportClients}
            className="flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Export
          </button>
          <Link
            to="/clients/new"
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Client
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search clients by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="new-client">New Client</option>
              <option value="active-client">Active</option>
              <option value="target">Target</option>
              <option value="placed">Placed</option>
              <option value="follow-up">Follow-up</option>
            </select>
          </div>

          {/* Care Level Filter */}
          <div>
            <select
              value={careLevelFilter}
              onChange={(e) => setCareLevelFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Care Levels</option>
              <option value="independent">Independent</option>
              <option value="assisted">Assisted Living</option>
              <option value="memory-care">Memory Care</option>
              <option value="skilled-nursing">Skilled Nursing</option>
            </select>
          </div>
        </div>

        {/* Sort Options */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-200">
          <span className="text-sm text-gray-600">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="updatedAt">Last Updated</option>
            <option value="createdAt">Date Added</option>
            <option value="firstName">First Name</option>
            <option value="lastName">Last Name</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
          >
            {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">
          Showing {filteredClients.length} of {(clientsData as any)?.data?.data?.pagination?.totalClients || 0} clients
        </p>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm bg-primary-100 text-primary-800 rounded">
              {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Client Grid */}
      {filteredClients.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <UserIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No clients found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || statusFilter !== 'all' || careLevelFilter !== 'all'
              ? 'Try adjusting your search criteria or filters'
              : 'Get started by adding your first client'}
          </p>
          <Link
            to="/clients/new"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add First Client
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredClients.map((client: Client) => {
            // Defensive check to ensure client object is valid
            if (!client || !client._id) return null;
            
            return (
            <div
              key={client._id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200"
            >
              {/* Client Card Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm">
                      {client.firstName} {client.lastName}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Added {formatDate(client.createdAt)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    {getStatusBadge(client.trackerStatus)}
                  </div>
                </div>
                {/* Remove care level badge since field doesn't exist in backend */}
              </div>

              {/* Client Details */}
              <div className="p-4 space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <PhoneIcon className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="truncate">{client.phone}</span>
                </div>
                
                <div className="flex items-center text-sm text-gray-600">
                  <MapPinIcon className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="truncate">
                    {client.address.city}, {client.address.state}
                  </span>
                </div>

                <div className="flex items-center text-sm text-gray-600">
                  <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="truncate">
                    Budget: {formatBudget(client)}
                  </span>
                </div>

                {client.medicalNotes && client.medicalNotes.trim() && (
                  <div className="text-xs text-gray-500">
                    <span className="font-medium">Medical Notes:</span>{' '}
                    {client.medicalNotes.length > 100 
                      ? `${client.medicalNotes.substring(0, 100)}...`
                      : client.medicalNotes
                    }
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="p-4 pt-0">
                <div className="flex gap-2">
                  <Link
                    to={`/clients/${client._id}`}
                    className="flex-1 flex items-center justify-center px-3 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
                  >
                    <EyeIcon className="w-4 h-4 mr-1" />
                    View
                  </Link>
                  <Link
                    to={`/clients/${client._id}/edit`}
                    className="flex-1 flex items-center justify-center px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                  >
                    <PencilIcon className="w-4 h-4 mr-1" />
                    Edit
                  </Link>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientList;
